import CircleIcon from "@mui/icons-material/Circle";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItem,
    ListItemText,
    Typography
} from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { useAtom } from "jotai";
import { clickedCourseScheduleBoxIndexAtom } from "../../atoms/course";
import { DialogTheme } from "../../styles/DialogTheme";

import { format } from "date-fns";
import { RRule, rrulestr, datetime } from "rrule";
import { convertToRRuleString } from "../../utils/courseUtils";

const ValidateCourseDatesModal = ({ open, close, getValues }) => {
    const [clickedCourseScheduleBoxIndex, setClickedCourseScheduleBoxIndex] =
        useAtom(clickedCourseScheduleBoxIndexAtom);
    const theme = useTheme();
    const numberSelectorPath = `schedules.${clickedCourseScheduleBoxIndex}.numberSelector`;
    const startDatePath = `schedules.${clickedCourseScheduleBoxIndex}.rruleStartDate`;
    const endDatePath = `schedules.${clickedCourseScheduleBoxIndex}.rruleEndDate`;
    const numberSelector = getValues(numberSelectorPath) || {};
    const selectedDays = numberSelector.selectedDays || [];
    const rruleStartDate = getValues(startDatePath);
    const rruleEndDate = getValues(endDatePath);
    const sectionRruleString = convertToRRuleString(
        selectedDays,
        numberSelector.selectedNumber
    );
    const datesGroupedByWeek = [];
    if (rruleStartDate && rruleEndDate && sectionRruleString) {
        const results = [];

        let startDt = new Date(rruleStartDate);
        const end = new Date(rruleEndDate);
        end.setHours(23, 59, 59);
        let endDt = new Date(end);
        let rule;
        try {
            rule = new RRule({
                ...rrulestr(sectionRruleString, {
                    dtstart: startDt
                }).options,
                dtstart: datetime(
                    startDt.getFullYear(),
                    startDt.getMonth() + 1,
                    startDt.getDate(),
                    0,
                    0
                ),
                until: endDt
                    ? datetime(
                          endDt.getFullYear(),
                          endDt.getMonth() + 1,
                          endDt.getDate(),
                          23,
                          59
                      )
                    : undefined // endDt should always be there
            });
        } catch (e) {
            console.log("error in creating rrule", e);
        }
        const courseDateObjects = [];
        for (const dt of rule.all()) {
            courseDateObjects.push(
                new Date(
                    dt.getUTCFullYear(),
                    dt.getUTCMonth(),
                    dt.getUTCDate(),
                    0,
                    0,
                    0
                )
            );
            let formatted = format(dt, "yyyy-MM-dd");
            results.push(formatted);
        }
        let previousDay = 6;
        courseDateObjects.forEach((date, index) => {
            const day = date.getDay();
            if (day <= previousDay) {
                //new week
                datesGroupedByWeek[datesGroupedByWeek.length] = [date];
            } else {
                datesGroupedByWeek[datesGroupedByWeek.length - 1].push(date);
            }
            previousDay = day;
        });
    }

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <Dialog open={open} width="450px">
                <DialogTitle
                    sx={{
                        paddingBottom: "28px"
                    }}
                >
                    Course dates
                </DialogTitle>

                <DialogContent sx={{ minHeight: "200px" }}>
                    {datesGroupedByWeek?.length ? (
                        <Typography
                            variant="body1"
                            sx={{ marginBottom: "24px" }}
                        >
                            This course is scheduled to run on the following
                            dates:
                        </Typography>
                    ) : null}
                    {numberSelector?.selectedNumber === 1 ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "start",
                                gap: 2
                            }}
                        >
                            {datesGroupedByWeek?.map(
                                (weeksDates, weekindex) => (
                                    <Box
                                        key={weekindex}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "start",
                                            gap: 0.25
                                        }}
                                    >
                                        {weeksDates.map((cd, cdIndex) => (
                                            <ListItem sx={{ margin: 0 }}>
                                                <CircleIcon
                                                    sx={{
                                                        width: "0.25rem",
                                                        height: "0.25rem",
                                                        color: theme.palette
                                                            .text.secondary
                                                    }}
                                                />
                                                <ListItemText
                                                    sx={{ margin: 0 }}
                                                    primary={`Week ${weekindex + 1} – ${format(
                                                        cd,
                                                        "EEE, MMM dd, yyyy"
                                                    )}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </Box>
                                )
                            )}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "start",
                                gap: 0.5
                            }}
                        >
                            {datesGroupedByWeek?.map((weeksDates, aindex) => (
                                <>
                                    {weeksDates.map((cd, cdIndex) => (
                                        <ListItem sx={{ margin: 0 }}>
                                            <CircleIcon
                                                sx={{
                                                    width: "0.25rem",
                                                    height: "0.25rem",
                                                    color: theme.palette.text
                                                        .secondary
                                                }}
                                            />
                                            <ListItemText
                                                sx={{ margin: 0 }}
                                                primary={format(
                                                    cd,
                                                    "EEE, MMM dd, yyyy"
                                                )}
                                            />
                                        </ListItem>
                                    ))}
                                </>
                            ))}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                    <Button
                        onClick={() => {
                            setClickedCourseScheduleBoxIndex();
                            close();
                        }}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};

export default ValidateCourseDatesModal;
